import { useState, useEffect, useCallback } from 'react'

/**
 * Default polling interval for mail count updates (3 seconds)
 */
export const MAIL_COUNT_POLLING_INTERVAL = 3000

/**
 * Active hours for real-time polling (UK time)
 * Polling only occurs between these hours (13:00 - 17:00 / 1 PM - 5 PM)
 */
export const MAIL_COUNT_ACTIVE_START_HOUR = 11
export const MAIL_COUNT_ACTIVE_END_HOUR = 15

/**
 * Check if current time is within active polling hours
 */
function isWithinActiveHours(): boolean {
  const now = new Date()
  const ukTime = new Date(
    now.toLocaleString('en-US', { timeZone: 'Europe/London' }),
  )
  const hour = ukTime.getHours()

  return (
    hour >= MAIL_COUNT_ACTIVE_START_HOUR && hour < MAIL_COUNT_ACTIVE_END_HOUR
  )
}

interface UseMailCountReturn {
  count: number | null
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

// Shared state across all hook instances
let sharedCount: number | null = null
let sharedError: Error | null = null
let sharedLoading = true
const sharedListeners: Set<() => void> = new Set()
let fetchPromise: Promise<void> | null = null
let pollingIntervalId: NodeJS.Timeout | null = null
let activePollingInterval = 0

/**
 * Notify all subscribed components of state changes
 */
function notifyListeners() {
  sharedListeners.forEach((listener) => listener())
}

/**
 * Shared fetch function - only one fetch happens at a time
 * Skips API call if outside active hours
 */
async function fetchMailCount(): Promise<void> {
  // If already fetching, return the existing promise
  if (fetchPromise) {
    return fetchPromise
  }

  // Skip fetch if outside active hours (unless this is the first fetch)
  if (sharedCount !== null && !isWithinActiveHours()) {
    return Promise.resolve()
  }

  fetchPromise = (async () => {
    try {
      const response = await fetch('/api/mail-count/', {
        cache: 'no-cache',
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch mail count (${response.status})`)
      }

      const data = await response.json()
      sharedCount = data.count
      sharedError = null
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error')
      sharedError = error
      console.error('Failed to fetch mail count:', error)
    } finally {
      sharedLoading = false
      fetchPromise = null
      notifyListeners()
    }
  })()

  return fetchPromise
}

/**
 * Setup shared polling interval
 */
function setupPolling(interval: number) {
  // If already polling at this interval, do nothing
  if (pollingIntervalId && activePollingInterval === interval) {
    return
  }

  // Clear existing interval
  if (pollingIntervalId) {
    clearInterval(pollingIntervalId)
    pollingIntervalId = null
  }

  // Setup new interval
  activePollingInterval = interval
  pollingIntervalId = setInterval(fetchMailCount, interval)
}

/**
 * Cleanup polling if no more listeners
 */
function cleanupPolling() {
  if (sharedListeners.size === 0 && pollingIntervalId) {
    clearInterval(pollingIntervalId)
    pollingIntervalId = null
    activePollingInterval = 0
  }
}

/**
 * Simple hook to fetch and poll mail count
 *
 * All instances share the same fetch to prevent duplicate requests.
 * Only one polling interval runs regardless of how many components use this hook.
 *
 * Polling occurs every 3 seconds (MAIL_COUNT_POLLING_INTERVAL).
 * API calls only happen during active hours (1-5 PM UK time).
 * Outside active hours, the interval still runs but skips API calls.
 *
 * @example
 * ```tsx
 * const { count, loading } = useMailCount()
 * ```
 */
export function useMailCount(): UseMailCountReturn {
  // Local state that syncs with shared state
  const [count, setCount] = useState<number | null>(sharedCount)
  const [loading, setLoading] = useState(sharedLoading)
  const [error, setError] = useState<Error | null>(sharedError)

  // Subscribe to shared state changes
  useEffect(() => {
    const updateState = () => {
      setCount(sharedCount)
      setLoading(sharedLoading)
      setError(sharedError)
    }

    // Add listener
    sharedListeners.add(updateState)

    // Initial fetch if needed (always fetch once, even outside active hours)
    if (sharedCount === null && !fetchPromise) {
      fetchMailCount()
    }

    // Setup polling - interval runs continuously but fetchMailCount checks active hours
    setupPolling(MAIL_COUNT_POLLING_INTERVAL)

    // Cleanup
    return () => {
      sharedListeners.delete(updateState)
      cleanupPolling()
    }
  }, [])

  return {
    count,
    error,
    loading,
    refetch: fetchMailCount,
  }
}
